import React from 'react';
import classNames from 'classnames';
import styles from './Pill.module.scss';
import ContactAvatar, { ContactAvatarGroup } from '@components/ContactAvatar';
import Contact from '@models/Contact';
import { useNavigate } from 'react-router-dom';
import { XIcon } from '@components/Icons';
import { useSelector } from 'react-redux';
import { selectContact, selectContactArray } from '@actions/contacts';

interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  contact?: Contact;
  contactGroup?: Array<Contact>;
  clickable?: boolean;
  editable?: boolean;
  maxWidth?: number;
}

const calculateMaxWidthForName = (
  maxWidth: number,
  editable: boolean,
  contact?: Contact,
): number => {
  let _maxWidth = maxWidth - 18;

  if (editable) {
    _maxWidth = _maxWidth - 17;
  }

  if (contact && contact.avatarURL) {
    _maxWidth = _maxWidth - 24;
  }

  return _maxWidth;
};

export const ConnectedPill = ({ contact, contactGroup, ...props }: PillProps) => {
  const _contact = contact ? useSelector(selectContact(contact.id)) : undefined;
  const _contactGroup = contactGroup
    ? useSelector(selectContactArray(contactGroup?.map((c) => c.id)))
    : undefined;

  return <Pill contact={_contact} contactGroup={_contactGroup} {...props} />;
};

export default function Pill({
  contact,
  contactGroup,
  maxWidth,
  clickable = false,
  editable = false,
  className = null,
  ...props
}: PillProps) {
  const navigate = useNavigate();
  className = classNames(styles.pill, (clickable || editable) && styles.clickable, className);
  maxWidth = maxWidth && calculateMaxWidthForName(maxWidth, editable, contact);
  let avatar = <ContactAvatar size="sm" className={styles.avatar} contact={contact} />;
  let label = contact?.displayName || contact?.display_name;

  if (clickable) {
    const onClickFromProps = props.onClick || (() => {});
    props.onClick = (e) => {
      onClickFromProps(e);
      navigate(`/contact/${contact.id}`);
    };
  }

  if (contactGroup) {
    avatar = <ContactAvatarGroup contacts={contactGroup} />;
    label = `${contactGroup.length} People`;
  }

  return (
    <span className={className} {...props}>
      {avatar}
      <span className={styles.name} style={maxWidth && { maxWidth }}>
        {label}
      </span>
      {!editable ? null : (
        <XIcon className={styles.remove} width={11} height={11} color="textSecondary" />
      )}
    </span>
  );
}
